/**
 * Shared component that renders a competitor-comparison page.
 * Used by `/vs/follow-up-boss` and `/vs/wise-agent`.
 *
 * D5.3 / D1.4 · Comparison pages highlighting the two differentiators
 * that consistently show up in agent forums: (1) e-signatures included,
 * (2) a genuine free-forever tier. Now with:
 *   - Pricing comparison block
 *   - "When to choose iKEY" section
 *   - Injected FAQPage JSON-LD (per-page questions)
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X, ArrowRight, Zap } from "lucide-react";
import { usePageSeo, usePageSchema } from "../lib/seo";

export default function VsCompare({
  competitor,
  testId,
  tagline,
  rows,
  pricing,
  chooseIkeyWhen,
  faq,
  slug,
  seoTitle,
  seoDescription,
}) {
  usePageSeo({
    title: seoTitle,
    description: seoDescription,
    canonical: slug ? `https://ikey-app.com/vs/${slug}` : undefined,
  });
  const faqSchema = faq && faq.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      }
    : null;
  usePageSchema(faqSchema);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={testId}>
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-slate-200" />
            <span className="font-heading text-lg font-bold tracking-tight">iKEY</span>
          </Link>
          <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>
          iKEY vs. {competitor}
        </div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.05]">
          {tagline}
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
          Two things agents keep hitting the wall on with {competitor}: <b>e-signatures live outside the tool</b>
          {" "}(so you pay DocuSign separately) and <b>there's no genuine free tier</b> to start with. iKEY solves both.
        </p>

        {/* ── Feature comparison ────────────────────────────────────── */}
        <div className="mt-10 rounded-xl ring-1 ring-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="p-4 font-heading font-bold text-slate-700">Feature</th>
                <th className="p-4 font-heading font-bold" style={{ color: "var(--primary-hex)" }}>iKEY</th>
                <th className="p-4 font-heading font-bold text-slate-700">{competitor}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.label}>
                  <td className="p-4 font-semibold text-slate-900">{r.label}</td>
                  <td className="p-4"><Cell v={r.ikey} /></td>
                  <td className="p-4"><Cell v={r.them} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pricing comparison ────────────────────────────────────── */}
        {pricing && (
          <section className="mt-14" data-testid={`${testId}-pricing`}>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Pricing, side by side.</h2>
            <p className="mt-2 text-sm text-slate-600">Monthly, USD. Every iKEY paid tier includes DocuSign — {competitor} doesn't.</p>
            <div className="mt-6 rounded-xl ring-1 ring-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="p-4 font-heading font-bold text-slate-700">Plan</th>
                    <th className="p-4 font-heading font-bold" style={{ color: "var(--primary-hex)" }}>iKEY</th>
                    <th className="p-4 font-heading font-bold text-slate-700">{competitor}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pricing.map((p) => (
                    <tr key={p.tier}>
                      <td className="p-4 font-semibold text-slate-900">{p.tier}</td>
                      <td className="p-4 text-slate-700">{p.ikey}</td>
                      <td className="p-4 text-slate-700">{p.them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── When to choose iKEY ──────────────────────────────────── */}
        {chooseIkeyWhen && (
          <section className="mt-14 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-6 sm:p-8" data-testid={`${testId}-choose-ikey`}>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5" style={{ color: "var(--primary-hex)" }} />
              <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">When to choose iKEY</h2>
            </div>
            <ul className="mt-5 space-y-3">
              {chooseIkeyWhen.map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-800 leading-relaxed">
                  <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── FAQ ──────────────────────────────────────────────────── */}
        {faq && faq.length > 0 && (
          <section className="mt-14" data-testid={`${testId}-faq`}>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Common questions</h2>
            <dl className="mt-6 space-y-3">
              {faq.map((it, i) => (
                <div key={i} className="rounded-xl bg-white ring-1 ring-slate-200 p-5">
                  <dt className="font-heading font-bold text-slate-900 text-base">{it.q}</dt>
                  <dd className="mt-2 text-sm text-slate-700 leading-relaxed">{it.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <div className="mt-14 flex items-center gap-3 flex-wrap">
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800"
          >
            Try iKEY free <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/#pricing"
            className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200"
          >
            See pricing
          </Link>
        </div>
      </main>

      <footer className="border-t border-slate-200 py-10">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} iKEY, Inc. · Comparison based on publicly listed features as of Feb 2026. {competitor} is a trademark of its respective owner.
        </div>
      </footer>
    </div>
  );
}

function Cell({ v }) {
  if (v === true)  return <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold"><Check className="h-4 w-4" /> Included</span>;
  if (v === false) return <span className="inline-flex items-center gap-1.5 text-rose-700 font-semibold"><X     className="h-4 w-4" /> Not included</span>;
  return <span className="text-slate-700">{v}</span>;
}
